import { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './lib/auth-context';
import { AppShell } from './components/AppShell';
import { QualificationProvider } from './contexts/QualificationContext';

import LoginPage from './pages/LoginPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import SetupAccountPage from './pages/SetupAccountPage';
import DashboardPage from './pages/DashboardPage';
import AgentsPage from './pages/AgentsPage';
import AgentDetailPage from './pages/AgentDetailPage';
import PipelinePage from './pages/PipelinePage';
import LeadsPage from './pages/LeadsPage';
import LeadDetailPage from './pages/LeadDetailPage';
import NewLeadPage from './pages/NewLeadPage';
import PropertiesPage from './pages/PropertiesPage';
import DocumentsPage from './pages/DocumentsPage';
import WhatsAppPage from './pages/WhatsAppPage';
import SettingsPage from './pages/SettingsPage';
import KpiTargetsSettingsPage from './pages/KpiTargetsSettingsPage';
import ProposalsLibraryPage from './pages/ProposalsLibraryPage';
import ProposalOrdersPage, { ProposalOrderDetailPage } from './pages/ProposalOrdersPage';
import PaymentsPage from './pages/PaymentsPage';
import StageAutomationsPage from './pages/StageAutomationsPage';
import TrashPage from './pages/TrashPage';
import FunnelDatesPage from './pages/FunnelDatesPage';
import LostRecoveryPage from './pages/LostRecoveryPage';
import PropertyUnitsAdminPage from './pages/PropertyUnitsAdminPage';
import AgentCommissionsAdminPage from './pages/AgentCommissionsAdminPage';
import MyCommissionsPage from './pages/MyCommissionsPage';
import CalendarPage from './pages/CalendarPage';
import IncassiPage from './pages/IncassiPage';
import TicketsPage from './pages/TicketsPage';
import TranscriptsPage from './pages/TranscriptsPage';
import CoachPage from './pages/CoachPage';
import LeaderboardPage from './pages/LeaderboardPage';
import TrainingPage from './pages/TrainingPage';
// ★ Fase 8 — Sezione Contratti (CRM) + pagina pubblica di firma elettronica
import ContractsPage from './pages/ContractsPage';
import ContractSignPage from './pages/ContractSignPage';
// ★ Fase 8.T — Editor matrici a pagina intera (con import PDF)
import TemplateEditorPage from './pages/TemplateEditorPage';

import PublicLanding from './pages/public/PublicLanding';
import Modulo2Page from './pages/public/Modulo2Page';
import VslPage from './pages/public/VslPage';
import AgentFollowupPage from './pages/public/AgentFollowupPage';
import BookingPage from './pages/public/BookingPage';
import ResortDetailPage from './pages/public/ResortDetailPage';
import PrivacyPage from './pages/public/PrivacyPage';
import TermsPage from './pages/public/TermsPage';
import CookiePolicyPage from './pages/public/CookiePolicyPage';
import DsarPage from './pages/public/DsarPage';
import LangGuard from './components/LangGuard';
// ★ Fase 59 — PublicLandingES dismessa: /es apre il nuovo funnel /f/es.
// ★ Fase 43 — Funnel verticali /f/:code (it-al, it-lo, it-ba, es-id, es-th, en-id, en-al)
import FunnelLanding from './pages/public/FunnelLanding';
// ★ Fase 44 — Funnel IT-AL dedicato (slide cliente 01-05/09, in successione)
import FunnelITAL from './pages/public/funnels/FunnelITAL';
// ★ Fase 55 — Mini-landing prodotto dedicate (campagne dirette: Bali / Lombok)
import AssetMiniLanding from './pages/public/funnels/AssetMiniLanding';
import AlbaniaAssetLanding from './pages/public/funnels/AlbaniaAssetLanding';
import ThreeGardenLanding from './pages/public/funnels/ThreeGardenLanding';
// ★ Fase 58 — Language gate sulla root (dominio → scelta lingua → funnel)
import LanguageGate from './pages/public/LanguageGate';
// ★ Fase 59 — Funnel ES (landing multi-paese gemella del Funnel IT, in spagnolo)
import FunnelES from './pages/public/funnels/FunnelES';
// ★ Fase 65 — Funnel EN (struttura IT, testi inglesi, prezzi/regole ES)
import FunnelEN from './pages/public/funnels/FunnelEN';

/**
 * LangLandingRouter — decide quale landing renderizzare per il segmento `:lang`.
 * ★ 2026-08-27 (Phase 57.ES.1) — Funnel ES ha un design/copy completamente
 * differente dal Funnel IT/EN (dark/red, LATAM Equity), quindi usa un
 * componente dedicato `PublicLandingES`.
 */
function LangLandingRouter() {
  const { lang } = useParams();
  // ★ Fase 59 — anche la vecchia landing ES non esiste più: /es apre il
  //   NUOVO funnel /f/es (come /it → /f/it). Route figlie intatte.
  if (lang === 'es') return <Navigate to="/f/es" replace />;
  // ★ Fase 58 — il vecchio funnel IT non esiste più: /it apre il NUOVO
  //   funnel /f/it. Le route figlie (/it/booking, /it/resort/… ) restano.
  if (lang === 'it') return <Navigate to="/f/it" replace />;
  // ★ Fase 65 — /en apre il NUOVO funnel /f/en (come IT/ES).
  if (lang === 'en') return <Navigate to="/f/en" replace />;
  return <PublicLanding />;
}
import OrganicMagicLinkPage from './pages/OrganicMagicLinkPage';
import ZoomReportPage from './pages/public/ZoomReportPage';
import DailyReportPage from './pages/public/DailyReportPage';
import AffiliatesAdminPage from './pages/AffiliatesAdminPage';
import AffiliateDashboardPage from './pages/AffiliateDashboardPage';
import AffiliateLandingPage from './pages/public/AffiliateLandingPage';
import HandoffReportPage from './pages/public/HandoffReportPage';
import CookieBanner from './components/CookieBanner';
import AIChatBubble from './components/AIChatBubble';
import { ErrorBoundary } from './components/ErrorBoundary';
import i18n from './i18n';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">{i18n.t('common.loading', 'Caricamento…')}</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  // Force password change before granting access to any /app/* route.
  // The /change-password route itself is exempt (else infinite loop).
  if (user.must_change_password && loc.pathname !== '/change-password') {
    return <Navigate to="/change-password" state={{ from: loc }} replace />;
  }
  // ★ Affiliate role gate \u2014 affiliates ONLY see their own dashboard.
  // Block any access to /app/* (admin area) and redirect them to their
  // spectator dashboard. Conversely, prevent non-affiliates from landing
  // on /affiliate/dashboard.
  const isAffiliate = user.role === 'affiliate';
  const isAffiliateRoute = loc.pathname.startsWith('/affiliate/');
  if (isAffiliate && !isAffiliateRoute) {
    return <Navigate to="/affiliate/dashboard" replace />;
  }
  if (!isAffiliate && isAffiliateRoute) {
    return <Navigate to="/app" replace />;
  }
  return children;
}

function RequireAuthNoLockout({ children }) {
  /** Same as RequireAuth but does NOT redirect on must_change_password.
   *  Used by /change-password itself so a forced user can land there. */
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">{i18n.t('common.loading', 'Caricamento…')}</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* ── Homepage: ★ Fase 58 — LANGUAGE GATE ─────────────────────────
       * Il dominio (greenparadisehcf.info) atterra qui: scelta lingua
       * immediata → IT apre il NUOVO funnel /f/it, EN/ES le loro landing.
       */}
      <Route path="/" element={<LanguageGate />} />

      {/* ── ★ Fase 43/57 — FUNNEL VERTICALI ───────────────────────────── */}
      {/* ★ Fase 57 — la landing multi-paese è il FUNNEL IT (route PRIMA della generica) */}
      <Route path="/f/it" element={<FunnelITAL />} />
      {/* ★ Fase 69 — allineamento al funnel di riferimento (flow-builder-97):
          landing dedicate Albania per lingua + mini-landing Indonesia. */}
      <Route path="/f/it-al-na" element={<AlbaniaAssetLanding slug="it-al-na" />} />
      <Route path="/f/it-al-tg" element={<ThreeGardenLanding />} />
      <Route path="/f/es-al-na" element={<AlbaniaAssetLanding slug="es-al-na" />} />
      <Route path="/f/es-al-tg" element={<ThreeGardenLanding lang="es" />} />
      <Route path="/f/en-al-na" element={<AlbaniaAssetLanding slug="en-al-na" />} />
      <Route path="/f/en-al-tg" element={<ThreeGardenLanding lang="en" />} />
      {/* legacy → nuove landing dedicate */}
      <Route path="/f/it-al" element={<Navigate to="/f/it-al-na" replace />} />
      <Route path="/f/es-al" element={<Navigate to="/f/es-al-na" replace />} />
      <Route path="/f/en-al" element={<Navigate to="/f/en-al-na" replace />} />
      <Route path="/f/es-th" element={<Navigate to="/f/es" replace />} />
      <Route path="/f/en-th" element={<Navigate to="/f/en" replace />} />
      <Route path="/it/resort/narea" element={<Navigate to="/f/it-al-na" replace />} />
      <Route path="/it/resort/three-garden-qerret" element={<Navigate to="/f/it-al-tg" replace />} />
      <Route path="/es/resort/es-narea" element={<Navigate to="/f/es-al-na" replace />} />
      <Route path="/es/resort/es-three-garden-qerret" element={<Navigate to="/f/es-al-tg" replace />} />
      <Route path="/en/resort/en-narea" element={<Navigate to="/f/en-al-na" replace />} />
      <Route path="/en/resort/en-three-garden-qerret" element={<Navigate to="/f/en-al-tg" replace />} />
      <Route path="/f/it-ba" element={<AssetMiniLanding slug="it-ba" />} />
      <Route path="/f/it-lo" element={<AssetMiniLanding slug="it-lo" />} />
      <Route path="/f/it-bali" element={<Navigate to="/f/it-ba" replace />} />
      <Route path="/f/it-lombok" element={<Navigate to="/f/it-lo" replace />} />
      <Route path="/f/es" element={<FunnelES />} />
      <Route path="/f/es-ba" element={<AssetMiniLanding slug="es-ba" />} />
      <Route path="/f/es-lo" element={<AssetMiniLanding slug="es-lo" />} />
      <Route path="/f/en" element={<FunnelEN />} />
      <Route path="/f/en-ba" element={<AssetMiniLanding slug="en-ba" />} />
      <Route path="/f/en-lo" element={<AssetMiniLanding slug="en-lo" />} />
      <Route path="/f/:code" element={<FunnelLanding />} />

      {/* ── Funnel per lingua (Funnel IT / EN / ES) ─────────────────── */}
      <Route path="/:lang" element={<LangGuard />}>
        <Route index element={<LangLandingRouter />} />
        <Route path="resort/:slug" element={<ResortDetailPage />} />
        <Route path="modulo2" element={<Modulo2Page />} />
        <Route path="vsl/:variant" element={<VslPage />} />
        <Route path="booking" element={<BookingPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="cookie-policy" element={<CookiePolicyPage />} />
        <Route path="dsar" element={<DsarPage />} />
      </Route>

      {/* ── Backwards-compat: redirect vecchie URL non prefissate ──────
       * Chi ha in bookmark /privacy, /resort/lombok ecc. viene rediretto
       * al funnel della lingua correntemente attiva (fallback: it).
       */}
      <Route path="/resort/:slug" element={<ResortDetailPage />} />
      <Route path="/modulo2" element={<Modulo2Page />} />
      <Route path="/vsl/:variant" element={<VslPage />} />
      <Route path="/booking" element={<BookingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookie-policy" element={<CookiePolicyPage />} />
      <Route path="/dsar" element={<DsarPage />} />

      {/* ── Public routes non tradotte (token-based, restano invariate) ─ */}
      <Route path="/agent-followup/:token" element={<AgentFollowupPage />} />
      <Route path="/zoom-report/:token" element={<ZoomReportPage />} />
      <Route path="/daily-report/:token" element={<DailyReportPage />} />
      <Route path="/o/:token" element={<OrganicMagicLinkPage />} />
      <Route path="/aff/:code" element={<AffiliateLandingPage />} />
      <Route path="/handoff-report/:token" element={<HandoffReportPage />} />
      {/* ★ Fase 8 — Firma elettronica pubblica (token-based, nessun login) */}
      <Route path="/firma/:token" element={<ContractSignPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/setup-account/:token" element={<SetupAccountPage />} />
      <Route path="/change-password" element={<RequireAuthNoLockout><ChangePasswordPage /></RequireAuthNoLockout>} />

      {/* Affiliate dashboard \u2014 separate from /app shell (clean spectator view) */}
      <Route
        path="/affiliate/dashboard"
        element={<RequireAuth><AffiliateDashboardPage /></RequireAuth>}
      />
      <Route path="/app" element={<RequireAuth><AppShell><DashboardPage /></AppShell></RequireAuth>} />
      {/* ★ 2026-08-29 (Fase 58.M) — Training gamificato agenti */}
      <Route path="/app/training" element={<RequireAuth><AppShell><TrainingPage /></AppShell></RequireAuth>} />
      <Route path="/app/pipeline" element={<RequireAuth><AppShell><PipelinePage /></AppShell></RequireAuth>} />
      <Route path="/app/leads" element={<RequireAuth><AppShell><LeadsPage /></AppShell></RequireAuth>} />
      <Route path="/app/leads/new" element={<RequireAuth><AppShell><NewLeadPage /></AppShell></RequireAuth>} />
      <Route path="/app/leads/:id" element={<RequireAuth><AppShell><LeadDetailPage /></AppShell></RequireAuth>} />
      <Route path="/app/properties" element={<RequireAuth><AppShell><PropertiesPage /></AppShell></RequireAuth>} />
      <Route path="/app/documents" element={<RequireAuth><AppShell><DocumentsPage /></AppShell></RequireAuth>} />
      {/* ★ Fase 8 — Sezione Contratti (matrici + invii, admin & senior) */}
      <Route path="/app/contracts" element={<RequireAuth><AppShell><ContractsPage /></AppShell></RequireAuth>} />
      {/* ★ Fase 8.T — Editor matrice a pagina intera (nuova / modifica) */}
      <Route path="/app/contracts/matrice/nuova" element={<RequireAuth><AppShell><TemplateEditorPage /></AppShell></RequireAuth>} />
      <Route path="/app/contracts/matrice/:templateId" element={<RequireAuth><AppShell><TemplateEditorPage /></AppShell></RequireAuth>} />
      {/* ★ 2026-08-11 — Vecchia pagina "Sequences" rimossa. Redirect al nuovo Stage Automations. */}
      <Route path="/app/sequences" element={<Navigate to="/app/email-automations" replace />} />
      <Route path="/app/whatsapp" element={<RequireAuth><AppShell><WhatsAppPage /></AppShell></RequireAuth>} />
      <Route path="/app/calendar" element={<RequireAuth><AppShell><CalendarPage /></AppShell></RequireAuth>} />
      <Route path="/app/agents" element={<RequireAuth><AppShell><AgentsPage /></AppShell></RequireAuth>} />
      <Route path="/app/agents/:agentId" element={<RequireAuth><AppShell><AgentDetailPage /></AppShell></RequireAuth>} />
      <Route path="/app/incassi" element={<RequireAuth><AppShell><IncassiPage /></AppShell></RequireAuth>} />
      <Route path="/app/tickets" element={<RequireAuth><AppShell><TicketsPage /></AppShell></RequireAuth>} />
      <Route path="/app/transcripts" element={<RequireAuth><AppShell><TranscriptsPage /></AppShell></RequireAuth>} />
      <Route path="/app/coach" element={<RequireAuth><AppShell><CoachPage /></AppShell></RequireAuth>} />
      <Route path="/app/leaderboard" element={<RequireAuth><AppShell><LeaderboardPage /></AppShell></RequireAuth>} />
      <Route path="/app/affiliates" element={<RequireAuth><AppShell><AffiliatesAdminPage /></AppShell></RequireAuth>} />
      <Route path="/app/settings" element={<RequireAuth><AppShell><SettingsPage /></AppShell></RequireAuth>} />
      <Route path="/app/settings/kpi-targets" element={<RequireAuth><AppShell><KpiTargetsSettingsPage /></AppShell></RequireAuth>} />
      <Route path="/app/proposals-library" element={<RequireAuth><AppShell><ProposalsLibraryPage /></AppShell></RequireAuth>} />
      <Route path="/app/proposal-orders" element={<RequireAuth><AppShell><ProposalOrdersPage /></AppShell></RequireAuth>} />
      <Route path="/app/proposal-orders/:id" element={<RequireAuth><AppShell><ProposalOrderDetailPage /></AppShell></RequireAuth>} />
      <Route path="/app/payments" element={<RequireAuth><AppShell><PaymentsPage /></AppShell></RequireAuth>} />
      <Route path="/app/email-automations" element={<RequireAuth><AppShell><StageAutomationsPage /></AppShell></RequireAuth>} />
      <Route path="/app/trash" element={<RequireAuth><AppShell><TrashPage /></AppShell></RequireAuth>} />
      <Route path="/app/funnel-dates" element={<RequireAuth><AppShell><FunnelDatesPage /></AppShell></RequireAuth>} />
      <Route path="/app/lost-recovery" element={<RequireAuth><AppShell><LostRecoveryPage /></AppShell></RequireAuth>} />
      <Route path="/app/property-units" element={<RequireAuth><AppShell><PropertyUnitsAdminPage /></AppShell></RequireAuth>} />
      <Route path="/app/agent-commissions" element={<RequireAuth><AppShell><AgentCommissionsAdminPage /></AppShell></RequireAuth>} />
      <Route path="/app/my-commissions" element={<RequireAuth><AppShell><MyCommissionsPage /></AppShell></RequireAuth>} />

      {/* Legacy KPI / Report pages — redirect to unified Dashboard (2026-06-22) */}
      <Route path="/app/kpi" element={<Navigate to="/app" replace />} />
      <Route path="/app/my-report" element={<Navigate to="/app" replace />} />
      <Route path="/app/reports" element={<Navigate to="/app" replace />} />
      <Route path="/app/weekly-reports" element={<Navigate to="/app" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function PublicChatGate() {
  const loc = useLocation();
  // Hide on internal CRM (/app/*), login, modulo2, VSL, booking, reports.
  // ★ 2026-08-27 (Phase 57.ES.2) — Hide anche su /es/* perché il Funnel ES
  // ha un assistente dedicato (`EsAssistant`) in stile dark/red con knowledge
  // base ristretta ai prodotti ES.
  const path = loc.pathname || '/';
  if (
    path.startsWith('/app') ||
    path === '/login' ||
    path.startsWith('/modulo2') ||
    path.startsWith('/vsl/') ||
    path.startsWith('/booking') ||
    path.startsWith('/zoom-report/') ||
    path.startsWith('/daily-report/') ||
    path.startsWith('/firma/') ||
    path === '/es' ||
    path.startsWith('/es/') ||
    // ★ Fase 44 — i funnel verticali /f/* hanno ciascuno il PROPRIO assistente
    path.startsWith('/f/')
  ) {
    return null;
  }
  return <AIChatBubble enabled={true} />;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <QualificationProvider>
            <AppRoutes />
            <CookieBanner />
            <PublicChatGate />
            {/* ★ 2026-08-27 (Phase 57.C) — `FirstAccessLanguageModal`
                sostituito dalla splash lingua `LangSplash` a `/`. */}
            <Toaster position="top-right" richColors closeButton />
          </QualificationProvider>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
